import React from 'react';
import { AlertTriangleIcon, MaximizeIcon, SaveIcon } from 'lucide-react';
import { DscrStatus, HousingPayment, RentalUnit } from '../types/dscr';
import { dscrStatus, formatCurrency, formatRatio, totalHousingPayment, totalRent } from '../utils/dscr';
const DSCR_TEXT: Record<DscrStatus, string> = {
  strong: 'text-emerald-700',
  pass: 'text-emerald-700',
  marginal: 'text-[#f5a623]',
  fail: 'text-red-700'
};
interface DscrResultBarProps {
  units: RentalUnit[];
  payment: HousingPayment;
  dscr: number;
  isDirty: boolean;
  onSave: () => void;
  onPopOut?: () => void;
}
export function DscrResultBar({
  units,
  payment,
  dscr,
  isDirty,
  onSave,
  onPopOut
}: DscrResultBarProps) {
  const status = dscrStatus(dscr);
  return <section aria-label="DSCR result" className="flex flex-wrap items-center justify-between gap-6 rounded-lg border border-slate-200 bg-white px-6 py-5 shadow-sm" data-id="element-7" data-mp="q0oqu7qa5ql9">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-8" data-mp="2rstakvxmlhkw">
        <div data-id="element-9" data-mp="2f9eeb4yzetau">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500" data-id="element-10" data-mp="2f9o8z5uz1omv">DSCR</p>
          <p className={`mt-1 text-4xl font-semibold tabular-nums leading-none ${DSCR_TEXT[status]}`} data-id="element-11" data-mp="2k8raigbqtcq3">
            {formatRatio(dscr)}
          </p>
        </div>

        <div className="flex items-stretch gap-3 md:ml-16" data-id="element-12" data-mp="2txj22ncw5v0h">
          <div className="rounded border border-slate-200 bg-slate-50 px-4 py-2.5" data-id="element-13" data-mp="2tyu2cd74okmu">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-14" data-mp="2xyb9rc647run">
              Rental Income
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900" data-id="element-15" data-mp="3300nx0omwlmy">
              {formatCurrency(totalRent(units))}
            </p>
          </div>

          <div aria-hidden="true" className="flex items-center text-xl text-slate-400" data-id="element-16" data-mp="3cv28dj7yszj7">
            ÷
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 px-4 py-2.5" data-id="element-17" data-mp="1dl3zf9rqw2es">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-18" data-mp="1iivxymmwamry">
              Housing Payment
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900" data-id="element-19" data-mp="15il7fhl2uk9b">
              {formatCurrency(totalHousingPayment(payment))}
            </p>
          </div>

          <div aria-hidden="true" className="flex items-center text-xl text-slate-400" data-id="element-20" data-mp="1kdrbw04iyb0j">
            =
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 px-4 py-2.5" data-id="element-21" data-mp="1tgiseec9bwz1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-22" data-mp="1teful84v4zjr">
              DSCR
            </p>
            <p className={`mt-1 text-lg font-semibold tabular-nums ${DSCR_TEXT[status]}`} data-id="element-23" data-mp="2362aviw25otd">
              {formatRatio(dscr)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3" data-id="element-24" data-mp="1y9le60asm24o">
        {isDirty ? <span className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-bold uppercase tracking-wide text-red-700" data-id="element-25" data-mp="2c79xh3p7p17m">
            <AlertTriangleIcon className="h-4 w-4" aria-hidden="true" data-id="element-26" data-mp="2qus4i8q8y7x7" />
            Unsaved changes made
          </span> : null}
        {onPopOut ? <button type="button" onClick={onPopOut} className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40" data-id="element-27" data-mp="2ht0z74zmc33v">
            <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-28" data-mp="2cuhef7wrgxd3" />
            Pop Out
          </button> : null}
        <button type="button" onClick={onSave} className="inline-flex items-center gap-2 rounded bg-[#0176d3] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus:ring-2 focus:ring-[#0176d3]/40 focus:ring-offset-2" data-id="element-29" data-mp="1z3klffyxae5i">
          <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-30" data-mp="1u250dnwqw7n4" />
          Save Scenario
        </button>
      </div>
    </section>;
}